import { Helmet } from "react-helmet-async";

export const SITE_URL = "https://agentstogo.ai";
export const SITE_NAME = "AgentsToGo";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/opengraph.jpg`;
export const CONTACT_EMAIL = "bluna@agentstogo.com";

export const absoluteUrl = (path: string): string => {
  if (!path || path === "/") return `${SITE_URL}/`;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

type JsonLd = Record<string, unknown>;

interface SeoProps {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: "website" | "article";
  noindex?: boolean;
  jsonLd?: JsonLd | JsonLd[];
}

export function Seo({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  noindex = false,
  jsonLd,
}: SeoProps) {
  const canonical = absoluteUrl(path);
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow"}
      />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {schemas.map((schema, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export const organizationSchema: JsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}/favicon.png`,
  image: DEFAULT_OG_IMAGE,
  slogan: "The wAI to go",
  description:
    "AgentsToGo is a done-for-you service that builds, integrates, and deploys custom AI agents for businesses — covering support, sales, finance, compliance, recruiting, and operations workflows.",
  email: CONTACT_EMAIL,
  contactPoint: {
    "@type": "ContactPoint",
    email: CONTACT_EMAIL,
    contactType: "sales",
    availableLanguage: ["English"],
  },
};

export const websiteSchema: JsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  description:
    "Expert-built, fully managed AI agents for your business workflows.",
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: "en",
};

export function breadcrumbSchema(
  items: { name: string; path: string }[],
): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function faqPageSchema(faqs: { q: string; a: string }[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };
}

export function serviceSchema(
  agents: { name: string; role?: string; description?: string }[],
): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Custom AI Agent Development & Implementation",
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: "Worldwide",
    description:
      "Done-for-you design, integration, and deployment of custom AI agents for business workflows.",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "AI Agent Catalog",
      itemListElement: agents.map((agent) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: agent.name,
          ...(agent.role ? { category: agent.role } : {}),
          ...(agent.description ? { description: agent.description } : {}),
        },
      })),
    },
  };
}
